import ProductRebilling, { IProductRebilling, IProductResponse } from "../models/api/ProductRebilling"
import servicesConfig, { CustomAxiosHeaders } from '../config/services'

import { AxiosResponse } from 'axios'
import { i18n } from '@/localization'
import { requests } from '.'

// Configure request setup
const serviceName = 'ProductRebillingService'
const host = servicesConfig.SaaSBillingV2
const opts: CustomAxiosHeaders = { host, serviceName }
const request = (addlOpts?: CustomAxiosHeaders) => {
  return requests(Object.assign({}, opts, addlOpts))
}

// Helper function to handle response
const responseBody = (res: AxiosResponse) => res.data

// Define service object with methods
const ProductRebillingService = {
  get: async (entityId: string, entityType: string, companyId?: string): Promise<IProductRebilling[]> => {
    try {
      let url = `/billing-config/${entityType}/${entityId}`
      if (entityType === 'LOCATION') {
        url = url + `?companyId=${companyId}`
      }

      const response = await request().get(url).then(responseBody)

      return response.data.map((row) => new ProductRebilling(row as IProductResponse))
    } catch (err) {
      throw err
    }
  },

  update: async (entityType: string, entityId: string, productRebilling: IProductRebilling, companyId?: string) => {
    try {
      let url = `/billing-config/${entityId}`
      if (entityType === 'LOCATION') {
        url = url + `?companyId=${companyId}`
      }
      const updateData = {
        entityType: entityType,
        product: productRebilling.type,
        config: {
          markup: productRebilling.markup,
          enabled: productRebilling.rebillingEnabled,
          basePrice: productRebilling.price,
          optIn: productRebilling.productOptIn
        }
      }

      await request().put(url, updateData).then(responseBody)
    } catch (err) {
      alert(err?.response?.data?.message || i18n.global.t('campaign.regulatoryBundle.errors.productRebilling.requestFailed'))
      throw err
    }
  },

  getRebilling: async (companyId: string): Promise<IProductRebilling[]> => {
    try {
      const url = `/rebilling-config/${companyId}`
      const { data } = await request().get(url).then(responseBody)
      const rebillingData = data.map((row) => new ProductRebilling(row as IProductResponse))
      return rebillingData
    } catch (err) {
      throw err
    }
  },

  updateRebilling: async (companyId: string, productRebilling: IProductRebilling) => {
    try {
      const updateData = {
        product: productRebilling.type,
        config: {
          markup: productRebilling.markup,
          enabled: productRebilling.rebillingEnabled,
          basePrice: productRebilling.price,
          optIn: productRebilling.productOptIn
        }
      }
      await request().put(`/rebilling-config/${companyId}`, updateData).then(responseBody)
    } catch (err) {
      alert(err?.response?.data?.message || i18n.global.t('campaign.regulatoryBundle.errors.productRebilling.requestFailed'))
      throw err
    }
  },

  getByProduct: async (entityId: string, entityType: string, product: string, addOptInParam: boolean = false): Promise<IProductRebilling> => {
    try {
      let url = `/billing-config/${entityType}/${entityId}/${product}`
      if (addOptInParam) {
        url = url + "?optIn=true"
      }
      const { data } = await request().get(url).then(responseBody)
      const rebillingData = new ProductRebilling(data[0] as IProductResponse)

      return rebillingData
    } catch (err) {
      throw err
    }
  },

  getByProductAndLocation: async (
    locationId: string,
    companyId: string,
    product: string): Promise<IProductResponse | undefined> => {
    try {
      const url = `/billing-config/LOCATION/${locationId}/${product}?companyId=${companyId}&optIn=true`

      const { data } = await request().get(url).then(responseBody)
      const productConfig = data[0]

      if (productConfig)
        return productConfig as IProductResponse
    } catch (err) {
      throw err
    }
  }
}

export default ProductRebillingService
